import React, { ComponentProps, useContext, useMemo, useRef } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { SalesTransaction } from '../../graphql/entities';
import { FinancialTransactionModalFormatter } from '../../components/modals/FinancialTransactionModal';
import SalesTransactionNoteModalFormatter from './SalesTransactionNoteModalFormatter';
import Formatters from '../../components/grid-addons/formatters';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { ALLOW_VIEW } from '../../utils/OperationPermissions';
import getAuditModalProps from '../../utils/getAuditModalProps';
import CurrencyContext from '../../components/Context/CurrencyContext';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { Direction, ISalesTransaction } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import customerAttributeQuery from '../../graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { useSalesTransactionWorkspaceActions } from '../../actions/salesLedger/SalesTransactionWorkspaceAction';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import SalesTransactionBalanceCards from './SalesTransactionWorkspaceBalanceCards';

const LazyFinancialAuditModal = React.lazy(() => import('../../components/modals/FinancialAuditModal'));
const LazyCustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));

const { workspace, type, title } = SalesTransaction;

const SALES_TRANSACTION_FIELD_NAMES: Array<Paths<ISalesTransaction>> = [
  'transactionNumber',
  'documentDate',
  'documentType',
  'documentNumber',
  'documentReference',
  'customer.reference',
  'amount',
  'outstandingAmount',
  'note',
  'transactionNoteType',
];

const SalesTransactionWorkspaceActions = (
  props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'actionButtons'>
) => {
  const [actions, modals] = useSalesTransactionWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={actions} />
    </>
  );
};

const SalesTransactionWorkspace = (props) => {
  const { additionalFields } = props;
  const workspaceRef = useRef<any>();

  const columnOverrides: IWorkspaceColumnOverride = useMemo(
    () => ({
      transactionNumber: {
        name: 'Transaction No',
        formatter: ModalFormatter,
        options: {
          modalContent: LazyFinancialAuditModal,
          entityId: 'sourceId',
          modalProps: getAuditModalProps,
        },
        width: 120,
      },
      documentDate: {
        name: 'Date',
        key: 'documentDate',
        formatter: Formatters.DateFormatter,
        width: 110,
      },
      documentType: {
        name: 'Source Type',
        key: 'documentType',
        width: 140,
        options: fieldMetaData.SalesDocumentType,
        formatter: Formatters.EnumFormatter,
      },
      amount: {
        name: 'Amount',
        formatter: Formatters.NumericFormatter,
        width: 120,
        options: {
          format: { decimals: 2 },
        },
      },
      outstandingAmount: {
        name: 'Outstanding',
        formatter: Formatters.NumericFormatter,
        width: 120,
        options: {
          format: { decimals: 2 },
        },
      },
      createdAt: {
        name: 'Created At',
        key: 'createdAt',
        formatter: Formatters.DateTimeFormatter,
        width: 140,
      },
      documentNumber: {
        name: 'Source No',
        formatter: FinancialTransactionModalFormatter,
        width: 125,
        options: {
          entityId: 'sourceId',
        },
      },
      'customer.reference': {
        name: 'Customer',
        formatter: ModalFormatter,
        width: 125,
        options: {
          modalContent: LazyCustomerForm,
          modalProps: { forceReadOnly: true },
          entityId: 'customer.id',
        },
      },
      'deliveryCustomer.reference': {
        name: 'Delivery Customer',
        width: 125,
      },
      'customer.customerAccountManagementGroup.description': {
        name: 'Customer Account Management Group',
      },
      'deliveryCustomer.customerAccountManagementGroup.description': {
        name: 'Delivery Customer Account Management Group',
      },
      documentReference: {
        name: 'Document Reference',
      },
      note: {
        name: 'Note',
        formatter: SalesTransactionNoteModalFormatter,
      },
      transactionNoteType: {
        name: 'Note Reason',
        key: 'transactionNoteType',
        formatter: Formatters.EnumFormatter,
        width: 100,
        options: fieldMetaData.TransactionNoteType,
      },
      baseVatAmount: {
        name: 'Base VAT Amount',
      },
      baseVatAmountLessSettlementDiscount: {
        name: 'Base VAT Amount Less Settlement Discount',
      },
      baseVatDiscountAmount: {
        name: 'Base VAT Discount Amount',
      },
      vatAmount: {
        name: 'VAT Amount',
      },
      vatAmountLessSettlementDiscount: {
        name: 'VAT Amount Less Settlement Discount',
      },
      vatDiscountAmount: {
        name: 'VAT Discount Amount',
      },
      excludeFromAutomatedCreditControl: {
        name: 'Excluded from ACC',
        formatter: Formatters.BooleanFormatter,
      },
    }),
    []
  );

  const { currency } = useContext(CurrencyContext);

  const fieldNames: Array<Paths<ISalesTransaction>> = [...SALES_TRANSACTION_FIELD_NAMES, ...(additionalFields || [])];
  if (currency?.isoCode?.codeAlpha3 && currency.isoCode.codeAlpha3 !== 'GBP') {
    fieldNames.push('baseAmount', 'baseOutstandingAmount');
  }

  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
    attributeNamePrefix: 'Customer - ',
  });

  const deliveryCustomerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'deliveryCustomer.attributeValues',
    columnKey: 'deliveryCustomer.attributeValues.attributeConfig.title',
    attributeNamePrefix: 'Delivery Customer - ',
  });

  const customColumns = useMemo(
    () => ({ ...(props.customColumns || {}), ...customerAttributeColumns, ...deliveryCustomerAttributeColumns }),
    [props.customColumns, customerAttributeColumns, deliveryCustomerAttributeColumns]
  );

  const workspaceProps = useWorkspace<ISalesTransaction>(props, SalesTransaction, {
    columns: fieldNames,
    columnOverrides: columnOverrides,
    initialSortColumn: 'transactionNumber',
    initialSortDirection: Direction.Desc,
    customColumns: customColumns,
  });

  return (
    <Workspace
      ref={workspaceRef}
      entityType={type}
      title={title}
      defaultQuery={workspace}
      permittedOperations={ALLOW_VIEW}
      {...workspaceProps}
      {...props}
      entity={SalesTransaction}
      dynamicDefaultColumns={true}
      workspaceActionButtonsHOC={SalesTransactionWorkspaceActions}
      additionalCards={<SalesTransactionBalanceCards />}
    />
  );
};

export default SalesTransactionWorkspace;
